import { useSnackbar } from 'notistack';
import { useApi } from '../../../shared/context';
import { CompanySubscription } from '../../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { getResponseData } from '../../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { CerebroTextButton } from '../../../shared/components';
import { QueryKeys } from '../../../shared/hooks';

interface DeleteSubscriptionDialogProps {
	companyGuid: string | undefined;
	dealGuid: string | undefined;
	subscription: CompanySubscription | null;
	close: () => void;
}

export const DeleteCompanySubscriptionDialog: React.FC<DeleteSubscriptionDialogProps> = ({
	companyGuid,
	dealGuid,
	subscription,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (subscription !== null) deleteSubscription(subscription);
	};

	const { mutate: deleteSubscription } = useMutation<void, unknown, CompanySubscription>(
		subscriptionToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.Company.SpecificCompanySubscription(companyGuid, subscriptionToDelete.guid)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				client.invalidateQueries(QueryKeys.Company.SubscriptionTiers(companyGuid, subscription?.guid));
				snackbar.enqueueSnackbar('Company subscription successfully deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the company subscription.', {
					variant: 'error',
				});
			},
		}
	);

	return (
		<Dialog open={subscription !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>Are you sure you want to delete the company subscription? Deleting will also cancel & delete any e-signature requests.</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
